/**
 * Create / edit a Labor Category (RP-11.1). One dialog serves both, per the
 * ConstraintEditorDialog convention.
 *
 * The fields here are exactly RP-11.1's list. Mix % and Avg Internal Cost are absent
 * because they are Rate Card fields and the mix carries a cross-row invariant — see
 * src/schemas/rate-cards.ts. A new category is created inert (0.00% mix, no rates) and
 * [FIN] gives it a share on the card.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { Discipline } from "@/hooks/use-disciplines";
import {
  type LaborCategoryView,
  useCreateLaborCategory,
  useUpdateLaborCategory,
} from "@/hooks/use-rate-card";

/** Radix SelectItem forbids an empty-string value — this sentinel maps back to null. */
const NO_DISCIPLINE = "__none__";
const NO_RATING = "__unset__";

const schema = z
  .object({
    name: z.string().trim().min(1, "Name is required").max(80),
    description: z.string().trim().max(500),
    disciplineId: z.string(),
    isDefaultForDiscipline: z.boolean(),
    expectedRatingMin: z.string(),
    expectedRatingMax: z.string(),
  })
  .refine((v) => (v.expectedRatingMin === NO_RATING) === (v.expectedRatingMax === NO_RATING), {
    message: "Set both ends of the rating range, or neither",
    path: ["expectedRatingMax"],
  })
  .refine(
    (v) =>
      v.expectedRatingMin === NO_RATING ||
      Number(v.expectedRatingMin) <= Number(v.expectedRatingMax),
    { message: "The minimum cannot exceed the maximum", path: ["expectedRatingMax"] },
  );

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disciplines: Discipline[];
  category?: LaborCategoryView;
}

export function LaborCategoryEditorDialog({ open, onOpenChange, disciplines, category }: Props) {
  const isEdit = Boolean(category);
  const create = useCreateLaborCategory();
  const update = useUpdateLaborCategory();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      disciplineId: NO_DISCIPLINE,
      isDefaultForDiscipline: false,
      expectedRatingMin: NO_RATING,
      expectedRatingMax: NO_RATING,
    },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      name: category?.name ?? "",
      description: category?.description ?? "",
      disciplineId: category?.disciplineId ?? NO_DISCIPLINE,
      isDefaultForDiscipline: category?.isDefaultForDiscipline ?? false,
      expectedRatingMin: category?.expectedRatingMin?.toString() ?? NO_RATING,
      expectedRatingMax: category?.expectedRatingMax?.toString() ?? NO_RATING,
    });
  }, [open, category, form]);

  const selectedDiscipline = form.watch("disciplineId");
  const podEligible = selectedDiscipline !== NO_DISCIPLINE;

  function onSubmit(values: FormValues) {
    const payload = {
      name: values.name,
      description: values.description.trim() ? values.description : null,
      disciplineId: podEligible ? values.disciplineId : null,
      isDefaultForDiscipline: podEligible ? values.isDefaultForDiscipline : false,
      expectedRatingMin:
        podEligible && values.expectedRatingMin !== NO_RATING
          ? Number(values.expectedRatingMin)
          : null,
      expectedRatingMax:
        podEligible && values.expectedRatingMax !== NO_RATING
          ? Number(values.expectedRatingMax)
          : null,
    };
    if (category) {
      update.mutate({ id: category.id, ...payload }, { onSuccess: () => onOpenChange(false) });
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          form.reset();
          onOpenChange(false);
        },
      });
    }
  }

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Labor Category" : "Add Labor Category"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Renaming updates the display everywhere; Plan Lines reference the category, not its name."
              : "The category starts with a 0.00% mix and no rates. Give it a share of the mix and its bill rates on the Rate Card."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Solution Architect" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea rows={2} placeholder="Optional" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="disciplineId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Discipline</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NO_DISCIPLINE}>None — plan and cost only</SelectItem>
                      {disciplines
                        .filter((d) => d.active)
                        .map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.code} — {d.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Optional. A category is Pod-eligible — attachable to a Planned Pod Position —
                    only when it maps to a Discipline. Most priced categories do not.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {podEligible && (
              <>
                <FormField
                  control={form.control}
                  name="isDefaultForDiscipline"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start gap-3 space-y-0 rounded-md border p-3">
                      <FormControl>
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-input"
                        />
                      </FormControl>
                      <div className="space-y-0.5">
                        <FormLabel className="font-normal">
                          Default Labor Category for this Discipline
                        </FormLabel>
                        <FormDescription>
                          Used when a Pod Shape Position needs a category. At most one per
                          Discipline.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-3">
                  {(["expectedRatingMin", "expectedRatingMax"] as const).map((name) => (
                    <FormField
                      key={name}
                      control={form.control}
                      name={name}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {name === "expectedRatingMin" ? "Expected rating from" : "to"}
                          </FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value={NO_RATING}>Not set</SelectItem>
                              {[1, 2, 3, 4, 5].map((level) => (
                                <SelectItem key={level} value={String(level)}>
                                  {level}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? "Saving…" : isEdit ? "Save Changes" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
